import { useContext, useState } from 'react';
import { ModalContext } from '~/context/modal/modalContext';
import { setOrderStatus } from '../api/setOrderStatus';
import { EApiStatus } from '~/shared/api/entity';
import { EYMActions } from '~/processes/providers/yandexMetrika/entity';
import { useYandexHit } from '~/processes/providers/yandexMetrika/hooks/useYandexHit';
import { useNavigate } from 'react-router-dom';
import { ERoutes } from '~/processes/routes';
import { useStore } from '~/shared/hooks/useStore';
import { useTranslation } from 'react-i18next';
import { confirmCellPhone } from '../api/confirmCellPhone';
import { isRequireConfirmation } from '../lib/isRequireConfirmation';

type TAcceptOrder = [(code: number|null) => Promise<void>, boolean, boolean];

/**
 * Хук который подтверждает заказ
 * @param session - id сессии (CLinks.links.confirm_order)
 * @returns [confirmOrder, loading, error]
 */
export const useAcceptOrder = (session: string): TAcceptOrder => {
    const navigate = useNavigate();
    const { CAuthorization, CLinks } = useStore();
    const { ymHit } = useYandexHit();
    const { t } = useTranslation('components');
    const { show, hide } = useContext(ModalContext);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const { links } = CLinks;
    const isRequireConfirm = isRequireConfirmation();

    const showErrorModal = (errorType: 'verifyCode'|'accept') => {
        switch (errorType) {
        case 'verifyCode':
            show({
                title: t('confirm.modalVerifyCode.title'),
                text: t('confirm.modalVerifyCode.errorValue'),
            });
            break;
        case 'accept':
            show({
                title: t('confirm.modalAccept.title'),
                text: t('confirm.modalAccept.errorValue'),
            });
            break;
        }
       
        setError(true);
        setLoading(false);
    };

    const confirmOrder = async (code: number|null) => {
        setLoading(true);
        hide();

        try {
            if (isRequireConfirm) {
                if (!code) {
                    return showErrorModal('verifyCode');
                }

                const verifyCode = await confirmCellPhone(links.confirm_code, code);

                if (verifyCode.error !== EApiStatus.OK) {
                    return showErrorModal('verifyCode');
                }
            }

            const query = await setOrderStatus(session);

            if (query.error === EApiStatus.OK) {
                await CAuthorization.updateData();

                if ([links.cloud_payment, links.qr, links.general_pay, links.deposit_payment, links.bonus_payment].some(link => link)) {
                    navigate(ERoutes.PAYMENT);
                } else {
                    navigate(ERoutes.CONFIRMED);
                }
            }
            else {
                showErrorModal('accept');
                setError(true);
            }
            
            setLoading(false);
        } catch (error) {
            showErrorModal('accept');
        }

        ymHit(EYMActions.TO_SHOW_ACCEPT_ORDER_MODAL);
    };

    return [confirmOrder, loading, error];
};